/**
 * Purchase Order Line Items Extensions Client - Pure Composition Pattern
 *
 * Uses PurchaseOrderLineItemsClient for all HTTP infrastructure
 * Adds business logic extensions without inheritance
 */
import { PurchaseOrderLineItemsClient } from '../generated_clients/generated-purchase_order_line_items-api-client.js';
import type {
  PurchaseOrderLineItems,
  PurchaseOrderLineItemsInsert,
  PurchaseOrderLineItemsUpdate,
  PurchaseOrderLineItemsQueryOptions
} from '../generated_schemas/generated-purchase_order_line_items-schema.js';

/**
 * Pure composition - no inheritance conflicts
 */
export class PurchaseOrderLineItemsExtensionsClient {
  constructor(private purchaseOrderLineItemsClient: PurchaseOrderLineItemsClient) {}

  // Delegate all basic operations to PurchaseOrderLineItemsClient
  async getAll(params?: PurchaseOrderLineItemsQueryOptions): Promise<PurchaseOrderLineItems[]> {
    return this.purchaseOrderLineItemsClient.getAll(params);
  }

  async getById(id: string): Promise<PurchaseOrderLineItems> {
    return this.purchaseOrderLineItemsClient.getById(id);
  }

  async create(data: PurchaseOrderLineItemsInsert): Promise<PurchaseOrderLineItems> {
    return this.purchaseOrderLineItemsClient.create(data);
  }

  async update(id: string, data: PurchaseOrderLineItemsUpdate): Promise<PurchaseOrderLineItems> {
    return this.purchaseOrderLineItemsClient.update(id, data);
  }

  async delete(id: string): Promise<void> {
    return this.purchaseOrderLineItemsClient.delete(id);
  }

  async count(filter?: Record<string, any>): Promise<number> {
    return this.purchaseOrderLineItemsClient.count(filter);
  }

  async exists(id: string): Promise<boolean> {
    return this.purchaseOrderLineItemsClient.exists(id);
  }

  // Logging utility
  private log(message: string, data?: any): void {
    console.log(`[PurchaseOrderLineItemsExtensions] ${message}`, data || '');
  }
}

// Factory function
export const createPurchaseOrderLineItemsExtensions = (purchaseOrderLineItemsClient: PurchaseOrderLineItemsClient): PurchaseOrderLineItemsExtensionsClient =>
  new PurchaseOrderLineItemsExtensionsClient(purchaseOrderLineItemsClient);

// Export singleton
const purchaseOrderLineItemsClient = new PurchaseOrderLineItemsClient();
export const purchaseOrderLineItemsExtensions = createPurchaseOrderLineItemsExtensions(purchaseOrderLineItemsClient);
